/**
 * Short-lived record of what the user just deleted, so a stale read cannot put
 * it back on screen.
 *
 * Deleting is asynchronous end to end: the API queues the delete and returns
 * straight away, the Messenger service soft-deletes the row, and only after
 * that does the search index the message list is served from catch up. The
 * `messageDeleted` websocket event lands inside that gap and invalidates the
 * conversation, so the refetch it triggers can read an index that still holds
 * the message — it reappears for a few seconds and is gone again on reload.
 *
 * Filtering every conversation response against what was just deleted closes
 * that window. Entries expire on their own, so once the index has settled the
 * server is authoritative again.
 */

/** How long a delete keeps suppressing a message the server still returns. */
export const DELETE_SUPPRESSION_MS = 30_000;

/** messageId -> when the suppression lapses. */
const suppressedMessageIds = new Map<string, number>();

/** customerId -> messages created up to `deletedBefore` are suppressed. */
const suppressedConversations = new Map<
	string,
	{ deletedBefore: number; expiresAt: number }
>();

const pruneExpired = (now: number): void => {
	suppressedMessageIds.forEach((expiresAt, messageId) => {
		if (expiresAt <= now) {
			suppressedMessageIds.delete(messageId);
		}
	});

	suppressedConversations.forEach(({ expiresAt }, customerId) => {
		if (expiresAt <= now) {
			suppressedConversations.delete(customerId);
		}
	});
};

/** Records messages the server has accepted a delete for. */
export const suppressDeletedMessages = (messageIds: string[]): void => {
	const now = Date.now();

	pruneExpired(now);
	messageIds.forEach((messageId) =>
		suppressedMessageIds.set(messageId, now + DELETE_SUPPRESSION_MS),
	);
};

/**
 * Records a whole-conversation delete. The individual message ids are not
 * known here — the request wipes the thread server-side — so anything created
 * up to now is suppressed, which leaves messages that arrive afterwards alone.
 */
export const suppressDeletedConversation = (customerId: string): void => {
	const now = Date.now();

	pruneExpired(now);
	suppressedConversations.set(customerId, {
		deletedBefore: now,
		expiresAt: now + DELETE_SUPPRESSION_MS,
	});
};

/** Drops messages a recent delete covers from a conversation response. */
export const filterDeletedMessages = <T extends { messageId: string; created: string }>(
	customerId: string | null,
	messages: T[],
): T[] => {
	const now = Date.now();

	pruneExpired(now);

	if (!suppressedMessageIds.size && !suppressedConversations.size) {
		return messages;
	}

	const conversation = customerId ? suppressedConversations.get(customerId) : undefined;

	return messages.filter(({ messageId, created }) => {
		if (suppressedMessageIds.has(messageId)) {
			return false;
		}

		if (!conversation) {
			return true;
		}

		const createdAt = new Date(created).getTime();

		// An unparseable timestamp is not evidence the message was deleted, so
		// it stays rather than vanishing from an intact thread.
		return Number.isNaN(createdAt) || createdAt > conversation.deletedBefore;
	});
};

/**
 * Lifts the suppression on messages whose delete turned out to fail, so a
 * message that is still there is not hidden behind its own rollback.
 */
export const releaseSuppressedMessages = (messageIds: string[]): void => {
	messageIds.forEach((messageId) => suppressedMessageIds.delete(messageId));
};

/** Test seam — production code lets entries lapse on their own. */
export const clearDeletedMessageSuppression = (): void => {
	suppressedMessageIds.clear();
	suppressedConversations.clear();
};
